import { FC, useMemo } from 'react';
import moment from 'moment-timezone';

import { useHatDashboardPageContext } from '../../../../hooks/Pages/HatDashboardPage';
import LineChartCard from '../../../../components/Metrics/LineChartCard';

interface Coordinate {
    x: number;
    y: number;
}

interface Series {
    key: string;
    total: number;
    values: Coordinate[];
}

const HatPerformanceChartLineCard: FC = (): React.JSX.Element => {
    const { queries } = useHatDashboardPageContext();

    const _queries = ['TREND_IMPRESSIONS', 'TREND_VISITS'].map((type: string) => queries[type]);

    const isLoading = useMemo(() => _queries.some((q) => q?.isFetching && q?.isLoading), [_queries]);

    const impressionsData = useMemo(() => {
        return (
            queries?.TREND_IMPRESSIONS?.data?.flatMap((_series: Series) =>
                _series.values.map((_value: Coordinate) => _value.y)
            ) || []
        );
    }, [queries?.TREND_IMPRESSIONS?.data]);

    const visitsData = useMemo(() => {
        return (
            queries?.TREND_VISITS?.data?.flatMap((_series: Series) =>
                _series.values.map((_value: Coordinate) => _value.y)
            ) || []
        );
    }, [queries?.TREND_VISITS?.data]);

    const dateLabels = useMemo(() => {
        return (
            queries?.TREND_VISITS?.data?.flatMap((_series: Series) =>
                _series.values.map((_value: Coordinate) => moment(_value.x).format('MMM D'))
            ) || []
        );
    }, [queries?.TREND_VISITS?.data]);

    const series = [
        {
            name: 'Total Impressions',
            type: 'column',
            data: impressionsData,
        },
        {
            name: 'Total Visits',
            type: 'line',
            data: visitsData,
        },
    ];

    return (
        <LineChartCard
            loading={isLoading}
            label="Impressions vs. Visits"
            chartOptions={{
                chart: {
                    height: 400,
                    toolbar: {
                        show: false,
                    },
                },
                xaxis: {
                    categories: dateLabels,
                },
                yaxis: [
                    {
                        title: {
                            text: 'Total Impressions',
                        },
                    },
                    {
                        opposite: true,
                        title: {
                            text: 'Total Visits',
                        },
                    },
                ],
            }}
            chartSeries={series}
        />
    );
};

export default HatPerformanceChartLineCard;
